import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import { formatDate } from '../lib/formatDate';
import { 
  Users, Printer, Ticket, Wrench, TrendingUp, Calendar, 
  Filter, BarChart3, PieChart as PieChartIcon, Activity,
  Eye, ExternalLink, X, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TICK_STYLE = { fontSize: 12 };

const COLORS = ['#0ea5e9', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function StatistichePage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filtri
  const [dataInizio, setDataInizio] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [dataFine, setDataFine] = useState(() => new Date().toISOString().split('T')[0]);
  const [filtroTecnico, setFiltroTecnico] = useState('all');
  const [filtroCliente, setFiltroCliente] = useState('all');
  
  // Dati per filtri
  const [tecnici, setTecnici] = useState([]);
  const [clienti, setClienti] = useState([]);
  
  // Vista attiva
  const [vistaAttiva, setVistaAttiva] = useState('panoramica');
  
  // Dettaglio modal
  const [detailModal, setDetailModal] = useState({ open: false, type: '', title: '', data: [] });
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Vendite articoli + guadagno (solo super admin)
  const [venditeArticoli, setVenditeArticoli] = useState({ articoli: [], totali: null });
  // Funnel conversione preventivi
  const [funnel, setFunnel] = useState(null);

  const fetchInitialData = useCallback(async () => {
    try {
      const [tecniciRes, clientiRes] = await Promise.all([
        axios.get(`${API}/tecnici`),
        axios.get(`${API}/clienti`)
      ]);
      setTecnici(tecniciRes.data);
      setClienti(clientiRes.data);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        data_inizio: dataInizio,
        data_fine: dataFine
      });
      if (filtroTecnico !== 'all') params.append('tecnico_id', filtroTecnico);
      if (filtroCliente !== 'all') params.append('cliente_id', filtroCliente);

      const response = await axios.get(`${API}/statistiche?${params}`);
      setStats(response.data);

      // Vendite articoli con guadagno (parallelo)
      try {
        const venditeRes = await axios.get(`${API}/statistiche/vendite-articoli?${params}`);
        setVenditeArticoli(venditeRes.data || { articoli: [], totali: null });
      } catch (e) {
        setVenditeArticoli({ articoli: [], totali: null });
      }
      // Funnel preventivi
      try {
        const funnelRes = await axios.get(`${API}/statistiche/funnel-preventivi?${params}`);
        setFunnel(funnelRes.data || null);
      } catch (e) {
        setFunnel(null);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  }, [dataInizio, dataFine, filtroTecnico, filtroCliente]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Funzione per caricare i dettagli
  const loadDetails = async (type, filter = null) => {
    setLoadingDetail(true);
    try {
      let data = [];
      let title = '';
      
      switch(type) {
        case 'clienti':
          title = 'Lista Clienti';
          const clientiRes = await axios.get(`${API}/clienti`);
          data = clientiRes.data.map(c => ({
            id: c.id,
            titolo: c.ragione_sociale,
            sottotitolo: `P.IVA: ${c.partita_iva || '-'}`,
            dettaglio1: c.citta_legale || c.citta || '-',
            dettaglio2: c.telefono || '-',
            link: `/clienti/${c.id}`
          }));
          break;
          
        case 'impianti':
          title = filter === 'scadenza' ? 'Impianti in Scadenza' : 'Lista Impianti';
          const impiantiRes = await axios.get(`${API}/impianti`);
          let impiantiData = impiantiRes.data;
          
          if (filter === 'scadenza') {
            const today = new Date();
            const limit = new Date();
            limit.setDate(limit.getDate() + 30);
            impiantiData = impiantiData.filter(i => {
              if (!i.data_scadenza_verifica) return false;
              const scad = new Date(i.data_scadenza_verifica);
              return scad <= limit;
            });
          }
          
          data = impiantiData.map(i => {
            const cliente = clienti.find(c => c.id === i.cliente_id);
            return {
              id: i.id,
              titolo: `${i.tipo} - ${i.matricola}`,
              sottotitolo: `${i.marca || ''} ${i.modello || ''}`.trim() || '-',
              dettaglio1: cliente?.ragione_sociale || '-',
              dettaglio2: i.data_scadenza_verifica || '-',
              link: `/impianti/${i.id}`,
              badge: i.data_scadenza_verifica && new Date(i.data_scadenza_verifica) < new Date() ? 'SCADUTO' : null
            };
          });
          break;
          
        case 'tickets':
        case 'tickets_aperti':
        case 'tickets_chiusi':
          const ticketsRes = await axios.get(`${API}/tickets`);
          let ticketsData = ticketsRes.data;
          
          // Filtra per periodo
          ticketsData = ticketsData.filter(t => {
            const created = t.created_at?.substring(0, 10) || '';
            return created >= dataInizio && created <= dataFine;
          });
          
          if (type === 'tickets_aperti' || filter === 'aperto') {
            title = 'Tickets Aperti';
            ticketsData = ticketsData.filter(t => t.stato === 'aperto' || t.stato === 'in_lavorazione');
          } else if (type === 'tickets_chiusi' || filter === 'chiuso') {
            title = 'Tickets Chiusi';
            ticketsData = ticketsData.filter(t => t.stato === 'chiuso');
          } else if (filter) {
            title = `Tickets - ${filter}`;
            ticketsData = ticketsData.filter(t => t.stato?.toLowerCase().replace('_', ' ') === filter.toLowerCase());
          } else {
            title = 'Tutti i Tickets';
          }
          
          if (filtroTecnico !== 'all') {
            ticketsData = ticketsData.filter(t => t.tecnico_assegnato_id === filtroTecnico);
          }
          if (filtroCliente !== 'all') {
            ticketsData = ticketsData.filter(t => t.cliente_id === filtroCliente);
          }
          
          data = ticketsData.map(t => {
            const cliente = clienti.find(c => c.id === t.cliente_id);
            const tecnico = tecnici.find(tc => tc.id === t.tecnico_assegnato_id);
            return {
              id: t.id,
              titolo: `#${t.numero} - ${t.oggetto}`,
              sottotitolo: cliente?.ragione_sociale || '-',
              dettaglio1: tecnico ? `${tecnico.nome} ${tecnico.cognome}` : 'Non assegnato',
              dettaglio2: formatDate(t.created_at?.substring(0, 10) || '-'),
              link: `/tickets/${t.id}`,
              badge: t.stato?.replace('_', ' ').toUpperCase(),
              badgeColor: t.stato === 'chiuso' ? 'bg-green-500' : t.stato === 'aperto' ? 'bg-amber-500' : 'bg-blue-500',
              priorita: t.priorita
            };
          });
          break;
          
        case 'interventi':
          title = 'Lista Interventi';
          const interventiRes = await axios.get(`${API}/interventi`);
          let interventiData = interventiRes.data;
          
          // Filtra per periodo
          interventiData = interventiData.filter(i => {
            return i.data_intervento >= dataInizio && i.data_intervento <= dataFine;
          });
          
          if (filtroTecnico !== 'all') {
            interventiData = interventiData.filter(i => i.tecnico_id === filtroTecnico);
          }
          
          data = await Promise.all(interventiData.map(async (i) => {
            const tecnico = tecnici.find(tc => tc.id === i.tecnico_id);
            let ticketInfo = '';
            if (i.ticket_id) {
              try {
                const ticketRes = await axios.get(`${API}/tickets/${i.ticket_id}`);
                ticketInfo = `Ticket #${ticketRes.data.numero}`;
              } catch { ticketInfo = 'Ticket'; }
            }
            return {
              id: i.id,
              titolo: i.descrizione?.substring(0, 50) + (i.descrizione?.length > 50 ? '...' : '') || 'Intervento',
              sottotitolo: tecnico ? `${tecnico.nome} ${tecnico.cognome}` : '-',
              dettaglio1: ticketInfo || '-',
              dettaglio2: `${formatDate(i.data_intervento)} | ${i.durata_ore || 0}h`,
              link: i.ticket_id ? `/tickets/${i.ticket_id}` : null
            };
          }));
          break;
          
        default:
          data = [];
      }
      
      setDetailModal({ open: true, type, title, data });
    } catch (error) {
      console.error('Error loading details:', error);
      toast.error('Errore nel caricamento dei dettagli');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Card cliccabile
  const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
    <Card 
      className={`rounded-none border-l-4 transition-all ${onClick ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02]' : ''}`} 
      style={{ borderLeftColor: color }}
      onClick={onClick}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono uppercase text-muted-foreground">{title}</p>
            <p className="text-3xl font-black mt-1" style={{ color }}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="p-3 rounded-none" style={{ backgroundColor: `${color}20` }}>
              <Icon className="h-6 w-6" style={{ color }} />
            </div>
            {onClick && <Eye className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Custom Pie Click Handler
  const handlePieClick = (data, type) => {
    if (data && data.stato) {
      loadDetails('tickets', data.stato);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO STATISTICHE...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="statistiche-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-cyan-500" />
            Statistiche
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Clicca sui numeri o grafici per vedere i dettagli</p>
        </div>
      </div>

      {/* Filtri */}
      <Card className="rounded-none bg-gradient-to-r from-slate-50 to-cyan-50 dark:from-slate-900 dark:to-cyan-950">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase">Data Inizio</Label>
              <Input
                type="date"
                value={dataInizio}
                onChange={(e) => setDataInizio(e.target.value)}
                className="rounded-none"
                data-testid="stats-data-inizio"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Data Fine</Label>
              <Input
                type="date"
                value={dataFine}
                onChange={(e) => setDataFine(e.target.value)}
                className="rounded-none"
                data-testid="stats-data-fine"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Tecnico</Label>
              <Select value={filtroTecnico} onValueChange={setFiltroTecnico}>
                <SelectTrigger className="rounded-none" data-testid="stats-tecnico">
                  <SelectValue placeholder="Tutti i tecnici" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Tutti i tecnici</SelectItem>
                  {tecnici.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nome} {t.cognome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Cliente</Label>
              <Select value={filtroCliente} onValueChange={setFiltroCliente}>
                <SelectTrigger className="rounded-none" data-testid="stats-cliente">
                  <SelectValue placeholder="Tutti i clienti" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Tutti i clienti</SelectItem>
                  {clienti.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs per viste */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'panoramica', label: 'Panoramica', icon: Activity },
          { id: 'tickets', label: 'Tickets', icon: Ticket },
          { id: 'interventi', label: 'Interventi', icon: Wrench },
          { id: 'clienti', label: 'Clienti', icon: Users },
          { id: 'impianti', label: 'Impianti', icon: Printer },
          { id: 'documenti', label: 'Documenti', icon: ExternalLink },
        ].map(({ id, label, icon: Icon }) => (
          <Button
            key={id}
            variant={vistaAttiva === id ? 'default' : 'outline'}
            onClick={() => setVistaAttiva(id)}
            className="rounded-none font-mono uppercase text-xs"
            data-testid={`tab-${id}`}
          >
            <Icon className="h-4 w-4 mr-2" />
            {label}
          </Button>
        ))}
      </div>

      {stats && (
        <>
          {/* Panoramica */}
          {vistaAttiva === 'panoramica' && (
            <div className="space-y-6">
              {/* Cards principali - CLICCABILI */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Clienti Totali" 
                  value={stats.totali?.clienti || 0} 
                  icon={Users} 
                  color="#0ea5e9"
                  onClick={() => loadDetails('clienti')}
                />
                <StatCard 
                  title="Impianti" 
                  value={stats.totali?.impianti || 0} 
                  icon={Printer} 
                  color="#14b8a6"
                  subtitle={`${stats.impianti_in_scadenza || 0} in scadenza`}
                  onClick={() => loadDetails('impianti')}
                />
                <StatCard 
                  title="Tickets Periodo" 
                  value={stats.tickets?.totale_periodo || 0} 
                  icon={Ticket} 
                  color="#f59e0b"
                  subtitle={`${stats.tickets?.aperti || 0} aperti`}
                  onClick={() => loadDetails('tickets')}
                />
                <StatCard 
                  title="Interventi Periodo" 
                  value={stats.interventi?.totale_periodo || 0} 
                  icon={Wrench} 
                  color="#8b5cf6"
                  subtitle={`${stats.interventi?.ore_totali?.toFixed(1) || 0}h lavorate`}
                  onClick={() => loadDetails('interventi')}
                />
              </div>

              {/* Grafici */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Tickets per stato - CLICCABILE */}
                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                      <PieChartIcon className="h-4 w-4" />
                      Tickets per Stato
                      <span className="text-xs text-muted-foreground font-normal ml-2">(clicca per dettagli)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={stats.tickets?.per_stato || []}
                          dataKey="count"
                          nameKey="stato"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ stato, count }) => `${stato}: ${count}`}
                          onClick={(data) => handlePieClick(data)}
                          cursor="pointer"
                        >
                          {(stats.tickets?.per_stato || []).map((entry, index) => (
                            <Cell 
                              key={`cell-${entry.stato}`} 
                              fill={COLORS[index % COLORS.length]} 
                              className="hover:opacity-80 transition-opacity"
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Lista stati cliccabile */}
                    <div className="flex flex-wrap gap-2 mt-4 justify-center">
                      {(stats.tickets?.per_stato || []).map((item, idx) => (
                        <Button
                          key={item.stato}
                          variant="outline"
                          size="sm"
                          className="rounded-none text-xs"
                          onClick={() => loadDetails('tickets', item.stato)}
                        >
                          <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                          {item.stato}: {item.count}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Andamento mensile */}
                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Andamento Mensile
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={stats.andamento_mensile || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mese" tick={TICK_STYLE} />
                        <YAxis tick={TICK_STYLE} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="tickets" stroke="#f59e0b" strokeWidth={2} name="Tickets" />
                        <Line type="monotone" dataKey="interventi" stroke="#8b5cf6" strokeWidth={2} name="Interventi" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Vista Tickets */}
          {vistaAttiva === 'tickets' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard 
                  title="Totale Periodo" 
                  value={stats.tickets?.totale_periodo || 0} 
                  icon={Ticket} 
                  color="#f59e0b" 
                  onClick={() => loadDetails('tickets')}
                />
                <StatCard 
                  title="Aperti" 
                  value={stats.tickets?.aperti || 0} 
                  icon={Ticket} 
                  color="#ef4444" 
                  onClick={() => loadDetails('tickets_aperti')}
                />
                <StatCard 
                  title="Chiusi" 
                  value={stats.tickets?.chiusi || 0} 
                  icon={Ticket} 
                  color="#10b981" 
                  onClick={() => loadDetails('tickets_chiusi')}
                />
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Tickets per Priorità</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={stats.tickets?.per_priorita || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="priorita" tick={TICK_STYLE} />
                        <YAxis tick={TICK_STYLE} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#f59e0b" name="Tickets" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Top 5 Clienti per Tickets</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(stats.tickets?.top_clienti || []).slice(0, 5).map((item) => (
                        <div key={item.cliente} className="flex justify-between items-center p-2 bg-muted/50 hover:bg-muted cursor-pointer" onClick={() => loadDetails('tickets')}>
                          <span className="text-sm truncate flex-1">{item.cliente}</span>
                          <span className="font-mono font-bold text-amber-500">{item.count}</span>
                        </div>
                      ))}
                      {(!stats.tickets?.top_clienti || stats.tickets.top_clienti.length === 0) && (
                        <p className="text-sm text-muted-foreground">Nessun dato disponibile</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Vista Interventi */}
          {vistaAttiva === 'interventi' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard 
                  title="Totale Periodo" 
                  value={stats.interventi?.totale_periodo || 0} 
                  icon={Wrench} 
                  color="#8b5cf6" 
                  onClick={() => loadDetails('interventi')}
                />
                <StatCard 
                  title="Ore Lavorate" 
                  value={`${stats.interventi?.ore_totali?.toFixed(1) || 0}h`} 
                  icon={Calendar} 
                  color="#0ea5e9" 
                />
                <StatCard 
                  title="Media per Intervento" 
                  value={`${stats.interventi?.media_ore?.toFixed(1) || 0}h`} 
                  icon={Activity} 
                  color="#14b8a6" 
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Interventi per Tecnico</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={stats.interventi?.per_tecnico || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={TICK_STYLE} />
                        <YAxis dataKey="tecnico" type="category" width={100} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8b5cf6" name="Interventi" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Ore per Tecnico</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(stats.interventi?.ore_per_tecnico || []).map((item) => (
                        <div key={item.tecnico} className="flex justify-between items-center p-2 bg-muted/50">
                          <span className="text-sm truncate flex-1">{item.tecnico}</span>
                          <span className="font-mono font-bold text-violet-500">{item.ore?.toFixed(1)}h</span>
                        </div>
                      ))}
                      {(!stats.interventi?.ore_per_tecnico || stats.interventi.ore_per_tecnico.length === 0) && (
                        <p className="text-sm text-muted-foreground">Nessun dato disponibile</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Vista Clienti */}
          {vistaAttiva === 'clienti' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard 
                  title="Clienti Totali" 
                  value={stats.totali?.clienti || 0} 
                  icon={Users} 
                  color="#0ea5e9" 
                  onClick={() => loadDetails('clienti')}
                />
                <StatCard 
                  title="Nuovi nel Periodo" 
                  value={stats.clienti?.nuovi_periodo || 0} 
                  icon={TrendingUp} 
                  color="#10b981" 
                />
                <StatCard 
                  title="Con Abbonamento" 
                  value={stats.clienti?.con_abbonamento || 0} 
                  icon={Calendar} 
                  color="#f59e0b" 
                />
              </div>

              <Card className="rounded-none">
                <CardHeader>
                  <CardTitle className="text-sm font-mono uppercase">Clienti per Città (Top 10)</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.clienti?.per_citta || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="citta" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tick={TICK_STYLE} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0ea5e9" name="Clienti" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Vista Impianti */}
          {vistaAttiva === 'impianti' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard 
                  title="Impianti Totali" 
                  value={stats.totali?.impianti || 0} 
                  icon={Printer} 
                  color="#14b8a6" 
                  onClick={() => loadDetails('impianti')}
                />
                <StatCard 
                  title="In Scadenza (30gg)" 
                  value={stats.impianti_in_scadenza || 0} 
                  icon={Calendar} 
                  color="#ef4444" 
                  onClick={() => loadDetails('impianti', 'scadenza')}
                />
                <StatCard 
                  title="Nuovi nel Periodo" 
                  value={stats.impianti?.nuovi_periodo || 0} 
                  icon={TrendingUp} 
                  color="#10b981" 
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Impianti per Tipo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={stats.impianti?.per_tipo || []}
                          dataKey="count"
                          nameKey="tipo"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ tipo, count }) => `${tipo}: ${count}`}
                        >
                          {(stats.impianti?.per_tipo || []).map((entry, index) => (
                            <Cell key={`cell-${entry.tipo}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Top Marche</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(stats.impianti?.per_marca || []).slice(0, 5).map((item) => (
                        <div key={item.marca || 'ns'} className="flex justify-between items-center p-2 bg-muted/50">
                          <span className="text-sm truncate flex-1">{item.marca || 'Non specificata'}</span>
                          <span className="font-mono font-bold text-teal-500">{item.count}</span>
                        </div>
                      ))}
                      {(!stats.impianti?.per_marca || stats.impianti.per_marca.length === 0) && (
                        <p className="text-sm text-muted-foreground">Nessun dato disponibile</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Vista Documenti */}
          {vistaAttiva === 'documenti' && (
            <div className="space-y-6">
              {/* Cards principali */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="rounded-none border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs font-mono uppercase text-muted-foreground">Documenti Periodo</p>
                        <p className="text-3xl font-black">{stats.documenti?.totale_periodo || 0}</p>
                      </div>
                      <ExternalLink className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="rounded-none border-l-4 border-l-emerald-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs font-mono uppercase text-muted-foreground">Totale Importi</p>
                        <p className="text-3xl font-black text-emerald-600">€ {stats.documenti?.totale_importo?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="rounded-none border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs font-mono uppercase text-muted-foreground">Tipi Documento</p>
                        <p className="text-3xl font-black">{stats.documenti?.per_tipo?.length || 0}</p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Documenti per tipo */}
                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Documenti per Tipo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(stats.documenti?.per_tipo || []).map((item) => (
                        <div key={item.tipo} className="flex justify-between items-center p-2 bg-muted/50">
                          <div className="flex-1">
                            <span className="text-sm font-medium">{item.tipo}</span>
                            <p className="text-xs text-muted-foreground">€ {item.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}</p>
                          </div>
                          <Badge variant="outline" className="rounded-none font-mono">{item.count}</Badge>
                        </div>
                      ))}
                      {(!stats.documenti?.per_tipo || stats.documenti.per_tipo.length === 0) && (
                        <p className="text-sm text-muted-foreground">Nessun documento nel periodo</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Clienti per Documenti */}
                <Card className="rounded-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-mono uppercase">Top Clienti per Importo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(stats.documenti?.top_clienti || []).map((item) => (
                        <Link 
                          key={item.cliente_id} 
                          to={`/clienti/${item.cliente_id}`}
                          className="flex justify-between items-center p-2 bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                        >
                          <div className="flex-1">
                            <span className="text-sm font-medium">{item.cliente}</span>
                            <p className="text-xs text-muted-foreground">{item.count} documenti</p>
                          </div>
                          <span className="font-mono font-bold text-emerald-600">€ {item.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}</span>
                        </Link>
                      ))}
                      {(!stats.documenti?.top_clienti || stats.documenti.top_clienti.length === 0) && (
                        <p className="text-sm text-muted-foreground">Nessun cliente nel periodo</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Documenti Recenti */}
              <Card className="rounded-none">
                <CardHeader>
                  <CardTitle className="text-sm font-mono uppercase">Documenti Recenti</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="data-table w-full">
                      <thead>
                        <tr>
                          <th>Numero</th>
                          <th>Tipo</th>
                          <th>Oggetto</th>
                          <th>Cliente</th>
                          <th>Importo</th>
                          <th>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stats.documenti?.recenti || []).map((doc) => (
                          <tr key={doc.id} className="hover:bg-muted transition-colors">
                            <td className="font-mono">{doc.numero}</td>
                            <td>
                              <Badge variant="outline" className="rounded-none uppercase text-xs">
                                {doc.tipo}
                              </Badge>
                            </td>
                            <td className="truncate max-w-[200px]">{doc.oggetto}</td>
                            <td>{doc.cliente}</td>
                            <td className="font-mono text-emerald-600">€ {doc.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}</td>
                            <td className="font-mono text-xs">{doc.data}</td>
                          </tr>
                        ))}
                        {(!stats.documenti?.recenti || stats.documenti.recenti.length === 0) && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted-foreground py-8">
                              Nessun documento nel periodo
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Modal Dettagli */}
      <Dialog open={detailModal.open} onOpenChange={(open) => setDetailModal({ ...detailModal, open })}>
        <DialogContent className="max-w-3xl max-h-[80vh] rounded-none">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              {detailModal.title}
              <Badge variant="outline" className="ml-2 rounded-none font-mono">
                {detailModal.data.length} elementi
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
            </div>
          ) : (
            <ScrollArea className="h-[60vh] pr-4">
              <div className="space-y-2">
                {detailModal.data.map((item, idx) => (
                  <div 
                    key={item.id || idx}
                    className="p-4 border border-border hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold truncate">{item.titolo}</h4>
                          {item.badge && (
                            <Badge className={`rounded-none text-xs ${item.badgeColor || 'bg-primary'}`}>
                              {item.badge}
                            </Badge>
                          )}
                          {item.priorita && (
                            <Badge variant="outline" className={`rounded-none text-xs ${
                              item.priorita === 'urgente' ? 'border-red-500 text-red-500' :
                              item.priorita === 'alta' ? 'border-orange-500 text-orange-500' :
                              item.priorita === 'media' ? 'border-yellow-500 text-yellow-500' :
                              'border-green-500 text-green-500'
                            }`}>
                              {item.priorita}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{item.sottotitolo}</p>
                        <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                          <span>{item.dettaglio1}</span>
                          <span>•</span>
                          <span>{item.dettaglio2}</span>
                        </div>
                      </div>
                      {item.link && (
                        <Link to={item.link}>
                          <Button variant="ghost" size="icon" className="rounded-none opacity-0 group-hover:opacity-100 transition-opacity">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
                {detailModal.data.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    Nessun elemento trovato
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* === FUNNEL CONVERSIONE PREVENTIVI === */}
      {funnel && funnel.totale_emessi > 0 && (
        <div className="mt-8 border-t-2 border-cyan-500/40 pt-6" data-testid="funnel-preventivi-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-black tracking-tight">Funnel Preventivi</h2>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                Tasso di conversione preventivi inviati → accettati nel periodo
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Conversione</p>
              <p className={`text-4xl font-black ${funnel.tasso_conversione >= 60 ? 'text-emerald-500' : funnel.tasso_conversione >= 30 ? 'text-amber-500' : 'text-red-500'}`}>
                {funnel.tasso_conversione}%
              </p>
            </div>
          </div>

          {/* Funnel visual */}
          <div className="space-y-2 mb-4">
            <div className="rounded-md bg-cyan-500/10 border-l-4 border-cyan-500 p-3" style={{ width: '100%' }}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-mono uppercase text-cyan-700 dark:text-cyan-400">Inviati</span>
                  <p className="text-2xl font-black text-cyan-600 mt-0.5">{funnel.totale_emessi}</p>
                </div>
                <p className="text-sm font-mono text-cyan-700 dark:text-cyan-400">€{Number(funnel.valore_emessi).toFixed(2)}</p>
              </div>
            </div>
            <div className="flex justify-center"><div className="w-1 h-3 bg-slate-300"></div></div>
            <div className="rounded-md bg-emerald-500/10 border-l-4 border-emerald-500 p-3 mx-auto" style={{ width: `${Math.max(funnel.tasso_conversione, 30)}%` }}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-mono uppercase text-emerald-700 dark:text-emerald-400">Accettati</span>
                  <p className="text-2xl font-black text-emerald-600 mt-0.5">{funnel.totale_accettati}</p>
                </div>
                <p className="text-sm font-mono text-emerald-700 dark:text-emerald-400">€{Number(funnel.valore_accettati).toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">In attesa</p>
              <p className="text-xl font-black mt-1 text-amber-600">{funnel.totale_in_attesa}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">€{Number(funnel.valore_in_attesa).toFixed(2)}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Conversione valore</p>
              <p className="text-xl font-black mt-1 text-emerald-600">{funnel.tasso_valore}%</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">su totale incassabile</p>
            </div>
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Tempo medio</p>
              <p className="text-xl font-black mt-1 text-blue-600">{funnel.tempo_medio_accettazione_giorni} gg</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">dall'invio all'accettazione</p>
            </div>
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Ricavo medio</p>
              <p className="text-xl font-black mt-1 text-violet-600">€{funnel.totale_accettati > 0 ? Math.round(funnel.valore_accettati / funnel.totale_accettati) : 0}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">per preventivo accettato</p>
            </div>
          </div>

          {funnel.top_accettati && funnel.top_accettati.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Top accettazioni del periodo</p>
              <div className="rounded-md border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-mono uppercase">N.</th>
                      <th className="px-3 py-2 text-left text-[11px] font-mono uppercase">Cliente</th>
                      <th className="px-3 py-2 text-left text-[11px] font-mono uppercase">Oggetto</th>
                      <th className="px-3 py-2 text-left text-[11px] font-mono uppercase">Firmatario</th>
                      <th className="px-3 py-2 text-right text-[11px] font-mono uppercase">Valore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnel.top_accettati.map((t) => (
                      <tr key={t.id} className="border-b border-border last:border-b-0">
                        <td className="px-3 py-2 font-mono text-xs">{String(t.numero).padStart(3, '0')}</td>
                        <td className="px-3 py-2 text-foreground">{t.cliente_nome}</td>
                        <td className="px-3 py-2 max-w-md truncate text-muted-foreground">{t.oggetto}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{t.nome_firmatario || '-'}</td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-600 font-mono">€{Number(t.valore).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === GUADAGNO ARTICOLI VENDUTI (solo admin) === */}
      {venditeArticoli && venditeArticoli.totali && (
        <div className="mt-8 border-t-2 border-emerald-500/40 pt-6" data-testid="guadagno-articoli-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-black tracking-tight">Guadagno Articoli Venduti</h2>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                Calcolato da Fatture e DDT nel periodo · costo da Listino · visibile solo Admin
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Articoli diversi</p>
              <p className="text-2xl font-black mt-1">{venditeArticoli.totali.articoli_diversi}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Pezzi venduti</p>
              <p className="text-2xl font-black mt-1">{venditeArticoli.totali.pezzi_venduti}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Ricavo</p>
              <p className="text-2xl font-black mt-1 text-blue-600">€{Number(venditeArticoli.totali.ricavo_totale).toFixed(2)}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-3">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">Costo</p>
              <p className="text-2xl font-black mt-1 text-amber-600">€{Number(venditeArticoli.totali.costo_totale).toFixed(2)}</p>
            </div>
            <div className="rounded-md border-2 border-emerald-500 bg-emerald-500/10 p-3">
              <p className="text-[11px] font-mono uppercase text-emerald-700 dark:text-emerald-400">Guadagno</p>
              <p className="text-2xl font-black mt-1 text-emerald-600">€{Number(venditeArticoli.totali.guadagno_totale).toFixed(2)}</p>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-mono uppercase">Codice</th>
                  <th className="px-3 py-2 text-left text-[11px] font-mono uppercase">Descrizione</th>
                  <th className="px-3 py-2 text-right text-[11px] font-mono uppercase">Qta</th>
                  <th className="px-3 py-2 text-right text-[11px] font-mono uppercase">Prezzo medio</th>
                  <th className="px-3 py-2 text-right text-[11px] font-mono uppercase">Ricavo</th>
                  <th className="px-3 py-2 text-right text-[11px] font-mono uppercase">Costo</th>
                  <th className="px-3 py-2 text-right text-[11px] font-mono uppercase">Guadagno</th>
                </tr>
              </thead>
              <tbody>
                {venditeArticoli.articoli.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Nessuna vendita nel periodo</td></tr>
                ) : venditeArticoli.articoli.map((a, idx) => (
                  <tr key={`${a.codice || 'manual'}-${idx}`} className="border-b border-border last:border-b-0" data-testid={`guadagno-row-${a.codice || idx}`}>
                    <td className="px-3 py-2 font-mono text-xs">{a.codice || <span className="text-muted-foreground italic">manuale</span>}</td>
                    <td className="px-3 py-2 max-w-md truncate">{a.descrizione}</td>
                    <td className="px-3 py-2 text-right font-mono">{a.quantita_venduta}</td>
                    <td className="px-3 py-2 text-right font-mono">€{Number(a.prezzo_medio_uscita).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-600">€{Number(a.ricavo_totale).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-600">
                      {a.costo_totale !== null ? `€${Number(a.costo_totale).toFixed(2)}` : <span className="text-muted-foreground italic">n/d</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold">
                      {a.guadagno !== null ? (
                        <span className={a.guadagno >= 0 ? 'text-emerald-600' : 'text-red-600'}>€{Number(a.guadagno).toFixed(2)}</span>
                      ) : <span className="text-muted-foreground italic">n/d</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            "n/d" = prezzo di acquisto non impostato nel Listino. Inserisci il prezzo di acquisto in Listino → Articolo per attivare il calcolo del guadagno.
          </p>
        </div>
      )}
    </div>
  );
}
